import React, { useCallback } from 'react'

import Image from 'next/image'

import { EnvelopeSimple, LinkedinLogo } from '@phosphor-icons/react'
import { Column, Modal, Row, Text } from 'Components/UI'

import { TeamRecordFieldsFragment } from 'GraphQL/DatoCMS/TypedDocuments'

import { useResponsiveLayout } from 'Hooks'

import { Container, Divider, ImageHolder, StyledLink } from './styles'

type Props = {
  data: TeamRecordFieldsFragment
  isOpened?: boolean
  onClose?: () => void
}

function TeamMemberDetailsModal({ data, isOpened, onClose }: Props) {
  const { isMobile } = useResponsiveLayout()

  const handleClose = useCallback(() => {
    onClose?.()
  }, [onClose])

  if (!data) return null

  return (
    <Modal isOpen={isOpened} onClose={handleClose}>
      <Container
        flexDirection={isMobile ? 'column' : 'row'}
        gap={[0, 0, 9]}
        maxWidth={['90dvw', '90dvw', '90dvw', '90dvw', '1220px']}
      >
        <ImageHolder width={[1, 1, 1 / 2]}>
          <Image alt={data.photo?.alt ?? ''} fill src={data.photo?.url ?? ''} />
        </ImageHolder>

        <Column p={[5, 5, 7, 7, 9]} width={[1, 1, 1 / 2]}>
          <Text
            bold
            important
            mb={[2, 2, 6]}
            nowrap
            screen18={isMobile}
            screen24={!isMobile}
          >
            {data.name}
          </Text>

          <Row alignCenter gap={3} spaceBetween>
            <Text accent bold screen14={isMobile} screen20={!isMobile}>
              {`${data.jobPosition ?? ''}${
                data.handicap && ` | ${data.handicap}`
              }`}
            </Text>

            <Row alignCenter gap={3}>
              <StyledLink
                href={data.linkedin}
                rel="noopener noreferrer"
                target="_blank"
              >
                <LinkedinLogo />
              </StyledLink>

              <StyledLink
                href={`mailto:${data.email}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <EnvelopeSimple weight="regular" />
              </StyledLink>
            </Row>
          </Row>

          <Divider my={[3, 3, 3, 3, 3, 6]} />

          <Text
            body
            center={isMobile}
            preWrap
            screen14={isMobile}
            screen18={!isMobile}
          >
            {data.bioLong}
          </Text>
        </Column>
      </Container>
    </Modal>
  )
}

export default TeamMemberDetailsModal
